import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell } from 'react-icons/fa';
import { notificationAPI } from '../services/api';
import { useNotifications } from '../contexts/NotificationContext';
import resolveNotificationLink from '../utils/notificationLink';

/**
 * The inbox, in the page chrome.
 *
 * The badge reads from the shared count, which polls; the list is fetched only when the
 * panel is opened, because a list nobody is looking at is a request for nothing.
 *
 * Opening the panel marks the rows SEEN, which clears the badge. It does not mark them
 * read — a glance is not a decision, and the items stay bold until one is actually
 * opened. Clicking one marks it read and goes where it points.
 */

const PAGE = 8;

const fmtWhen = (d) => {
  if (!d) return '';
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return '';
  const mins = Math.round((Date.now() - t.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const NotificationBell = () => {
  const { unread, capped, markSeen, applyRead, refresh } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrap = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    notificationAPI.list({ limit: PAGE })
      .then((r) => setRows((r.data && r.data.data) || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // Close on a click outside or on Escape, so the panel never strands the page.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      load();
      // The badge answers to having looked; the rows still read as unread.
      markSeen().then(refresh);
    }
  };

  const openRow = (row) => {
    setOpen(false);
    if (!row.readAt) {
      notificationAPI.markRead(row._id).then(() => applyRead(1)).catch(() => {});
    }
    const link = resolveNotificationLink(row.entity && row.entity.url);
    if (!link) return;
    if (link.internal) navigate(link.to);
    else window.open(link.href, '_blank', 'noopener');
  };

  const readAll = () => {
    notificationAPI.markAllRead()
      .then(() => { applyRead(null); load(); })
      .catch(() => {});
  };

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <button
        type="button"
        className="btn btn-link p-1 position-relative"
        style={{ color: 'var(--text-muted)', lineHeight: 1 }}
        onClick={toggle}
        aria-label={unread ? `Notifications, ${unread}${capped ? '+' : ''} unread` : 'Notifications'}
        aria-expanded={open}
        title="Notifications"
      >
        <FaBell style={{ fontSize: '1rem' }} />
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              transform: 'translate(35%, -20%)',
              background: 'var(--color-error-500)',
              color: '#fff',
              borderRadius: 999,
              fontSize: '0.6rem',
              lineHeight: 1,
              padding: '2px 5px',
              fontWeight: 600,
            }}
          >
            {capped ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 4,
            width: 360,
            maxWidth: 'calc(100vw - 2rem)',
            zIndex: 1050,
            background: 'var(--surface-base)',
            border: '1px solid var(--border-secondary)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>Notifications</span>
            {rows.some((r) => !r.readAt) && (
              <button type="button" className="btn btn-link p-0" style={{ fontSize: '0.72rem' }} onClick={readAll}>
                Mark all read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {loading && (
              <div className="text-muted px-3 py-3" style={{ fontSize: '0.8rem' }}>Loading…</div>
            )}
            {!loading && !rows.length && (
              <div className="text-muted px-3 py-4 text-center" style={{ fontSize: '0.8rem' }}>
                Nothing yet. Work handed to you will wait here.
              </div>
            )}
            {!loading && rows.map((row) => (
              <button
                key={row._id}
                type="button"
                className="d-block w-100 text-start border-0 px-3 py-2"
                style={{
                  background: row.readAt ? 'transparent' : 'var(--surface-raised)',
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                }}
                onClick={() => openRow(row)}
              >
                <div
                  style={{ fontSize: '0.8rem', fontWeight: row.readAt ? 400 : 600, lineHeight: 1.25 }}
                >
                  {row.title}
                </div>
                {row.body && (
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>{row.body}</div>
                )}
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>
                  {fmtWhen(row.createdAt)}
                  {row.actorName ? ` · ${row.actorName}` : ''}
                </div>
              </button>
            ))}
          </div>

          <div className="px-3 py-2 border-top text-center">
            <button
              type="button"
              className="btn btn-link p-0"
              style={{ fontSize: '0.75rem' }}
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              See all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
